import { Controller } from "@hotwired/stimulus"

/**
 * RalphFixPlanController handles fix plan editing and task tracking.
 *
 * Provides interactive checkbox toggling, raw markdown editing,
 * and automatic saving via AJAX.
 */
export default class extends Controller {
  static values = {
    sessionId: String,
    updateUrl: String
  }

  static targets = [
    "checklistView",
    "rawView",
    "rawTextarea",
    "checklistBtn",
    "rawBtn",
    "progressBar",
    "completedCount",
    "totalCount",
    "percentage",
    "saveIndicator",
    "savingIndicator",
    "newTaskInput"
  ]

  connect() {
    console.log("[RalphFixPlan] Controller connected")
    this.isDirty = false
    this.originalContent = this.hasRawTextareaTarget ? this.rawTextareaTarget.value : ""
  }

  // View switching

  showChecklist(event) {
    event?.preventDefault()

    if (this.isDirty) {
      if (!confirm("You have unsaved changes. Discard them?")) {
        return
      }
      this.rawTextareaTarget.value = this.originalContent
      this.isDirty = false
    }

    this.checklistViewTarget.classList.remove("hidden")
    this.rawViewTarget.classList.add("hidden")
    this.checklistBtnTarget.classList.add("btn-active")
    this.rawBtnTarget.classList.remove("btn-active")
  }

  showRaw(event) {
    event?.preventDefault()

    this.checklistViewTarget.classList.add("hidden")
    this.rawViewTarget.classList.remove("hidden")
    this.checklistBtnTarget.classList.remove("btn-active")
    this.rawBtnTarget.classList.add("btn-active")
  }

  // Task management

  toggleTask(event) {
    const checkbox = event.target
    const lineNumber = parseInt(checkbox.dataset.lineNumber)
    const isChecked = checkbox.checked

    // Update visual state
    const label = checkbox.closest("label")
    const textSpan = label.querySelector("span")
    if (textSpan) {
      if (isChecked) {
        textSpan.classList.add("line-through", "text-base-content/50")
      } else {
        textSpan.classList.remove("line-through", "text-base-content/50")
      }
    }

    // Update fix plan content
    this.updateTaskInContent(lineNumber, isChecked)
    this.saveContent()
  }

  updateTaskInContent(lineNumber, isChecked) {
    const lines = this.rawTextareaTarget.value.split("\n")
    if (lineNumber >= 0 && lineNumber < lines.length) {
      const line = lines[lineNumber]
      if (isChecked) {
        lines[lineNumber] = line.replace(/- \[ \]/, "- [x]")
      } else {
        lines[lineNumber] = line.replace(/- \[[xX]\]/, "- [ ]")
      }
      this.rawTextareaTarget.value = lines.join("\n")
    }
  }

  addTask(event) {
    event?.preventDefault()

    if (!this.hasNewTaskInputTarget) return

    const taskText = this.newTaskInputTarget.value.trim()
    if (!taskText) return

    // Add new task to the end
    const currentContent = this.rawTextareaTarget.value
    const newTask = `- [ ] ${taskText}`

    if (currentContent.trim()) {
      this.rawTextareaTarget.value = currentContent + "\n" + newTask
    } else {
      this.rawTextareaTarget.value = newTask
    }

    // Clear input
    this.newTaskInputTarget.value = ""

    // Save and reload to show the new task
    this.saveContent().then(() => {
      window.location.reload()
    })
  }

  // Editing

  markDirty() {
    this.isDirty = true
  }

  cancelEdit(event) {
    event?.preventDefault()

    if (this.isDirty) {
      if (!confirm("Discard changes?")) {
        return
      }
    }

    this.rawTextareaTarget.value = this.originalContent
    this.isDirty = false
    this.showChecklist()
  }

  saveRaw(event) {
    event?.preventDefault()

    this.saveContent().then(() => {
      this.originalContent = this.rawTextareaTarget.value
      this.isDirty = false
      // Reload to show updated checklist
      window.location.reload()
    })
  }

  // API calls

  async saveContent() {
    this.showSaving()

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "Accept": "application/json"
        },
        body: JSON.stringify({
          ralph_session: {
            fix_plan: this.rawTextareaTarget.value
          }
        })
      })

      if (!response.ok) {
        throw new Error(`Save failed: ${response.statusText}`)
      }

      const data = await response.json()
      this.updateProgress(data)
      this.showSaved()
      return data
    } catch (error) {
      console.error("[RalphFixPlan] Save error:", error)
      this.showError("Failed to save changes")
      throw error
    }
  }

  updateProgress(data) {
    if (data.fix_plan_progress) {
      const { completed, total, percentage } = data.fix_plan_progress

      if (this.hasCompletedCountTarget) {
        this.completedCountTarget.textContent = completed
      }
      if (this.hasTotalCountTarget) {
        this.totalCountTarget.textContent = total
      }
      if (this.hasPercentageTarget) {
        this.percentageTarget.textContent = percentage
      }
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.value = percentage
        // Update progress bar class
        this.progressBarTarget.className = `progress w-full mb-4 ${this.progressClass(percentage)}`
      }
    }
  }

  progressClass(percentage) {
    if (percentage >= 100) return "progress-success"
    if (percentage >= 76) return "progress-accent"
    if (percentage >= 51) return "progress-info"
    if (percentage >= 26) return "progress-warning"
    return "progress-error"
  }

  // UI helpers

  showSaving() {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.classList.add("hidden")
    }
    if (this.hasSavingIndicatorTarget) {
      this.savingIndicatorTarget.classList.remove("hidden")
    }
  }

  showSaved() {
    if (this.hasSavingIndicatorTarget) {
      this.savingIndicatorTarget.classList.add("hidden")
    }
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.classList.remove("hidden")
      // Hide after 3 seconds
      setTimeout(() => {
        this.saveIndicatorTarget.classList.add("hidden")
      }, 3000)
    }
  }

  showError(message) {
    if (this.hasSavingIndicatorTarget) {
      this.savingIndicatorTarget.classList.add("hidden")
    }

    // Show toast notification
    const toast = document.createElement("div")
    toast.className = "alert alert-error shadow-lg fixed bottom-4 right-4 w-auto max-w-md z-50"
    toast.innerHTML = `
      <div>
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>${message}</span>
      </div>
      <button class="btn btn-sm btn-ghost" onclick="this.parentElement.remove()">×</button>
    `

    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 5000)
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
